/**
 * 📦 Bulk SMTP Creator Component
 * Erstellt mehrere SMTP-Server aus Domain-Liste (wie PHP-System)
 * Provider: normal, google, freemail (GMX/Mail.com/Mailo), outlook, rambler, tonline
 */

import React, { useState } from 'react';
import { 
  Server, 
  Eye,
  EyeOff,
  CheckCircle,
  X
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const BulkSmtpCreator = ({ onClose, onSuccess }) => {
  const [smtpType, setSmtpType] = useState('normal'); // 'normal', 'google', 'freemail', 'outlook', 'rambler', 'tonline'
  const [formData, setFormData] = useState({
    host: 'smtp-relay.brevo.com',
    port: 587,
    username: '',
    password: '',
    encryption: 'tls',
    domains: '',
    from_name: '',
    from_prefix: 'kontakt',
    google_credentials: '', // Format: email@gmail.com suwx nqas oeky maql
    freemail_credentials: '', // Format: GMX, Mail.com oder Mailo: email:password
    outlook_credentials: '', // Format: email@outlook.com:password
    rambler_credentials: '', // Format: email@rambler.ru:password
    tonline_credentials: '' // Format: email@t-online.de:password
  });
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [preview, setPreview] = useState([]);
  const normalConfigRef = React.useRef({
    host: 'smtp-relay.brevo.com',
    port: 587,
    encryption: 'tls',
    username: '',
    password: '',
    from_prefix: 'kontakt',
    from_name: ''
  });

  const parseGoogleCredentialLine = (line) => {
    const trimmed = String(line || '').trim();
    if (!trimmed) return null;

    if (trimmed.includes(':')) {
      const parts = trimmed.split(':');
      if (parts.length >= 5 && parts[0].includes('@')) {
        const email = parts[0].trim();
        const spacedAppPassword = parts.slice(4).join(':').trim();
        const compactToken = parts[3].trim();
        const appPassword = (spacedAppPassword || compactToken).replace(/\s+/g, '');
        if (email.includes('@') && appPassword.length >= 16) {
          return { email, appPassword };
        }
      }
    }

    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2 && parts[0].includes('@')) {
      const email = parts[0].trim();
      const appPassword = parts.slice(1).join(' ').replace(/\s+/g, '');
      if (appPassword.length >= 16) {
        return { email, appPassword };
      }
    }

    return null;
  };

  // Google SMTP Credentials parsen
  const parseGoogleCredentials = (text) => {
    const lines = text.trim().split('\n');
    const googleSmtps = [];

    lines.forEach(line => {
      const parsed = parseGoogleCredentialLine(line);
      if (!parsed) return;

      googleSmtps.push({
        email: parsed.email,
        username: parsed.email,
        password: parsed.appPassword,
        from_email: parsed.email,
        label: `Gmail - ${parsed.email}`,
        host: 'smtp.gmail.com',
        port: 587,
        encryption: 'tls'
      });
    });

    return googleSmtps;
  };

  const resolveGmxSmtpHost = (email) => {
    const e = String(email || '').trim().toLowerCase();
    return (e.endsWith('@gmx.com') || e.endsWith('@gmx.us'))
      ? 'mail.gmx.com'
      : 'mail.gmx.net';
  };

  const isGmxEmail = (email) => /@gmx(?:\.|-)/i.test(String(email || '').trim());
  const isMailComEmail = (email) => /@mail\.com$/i.test(String(email || '').trim());
  const mailoDomains = new Set([
    'mailo.com',
    'netcourrier.com',
    'netc.fr',
    'francemel.fr',
    'emailasso.net',
    'monemail.com',
  ]);
  const isMailoEmail = (email) => {
    const value = String(email || '').trim().toLowerCase();
    const at = value.lastIndexOf('@');
    return at >= 0 && mailoDomains.has(value.slice(at + 1));
  };

  const parseFreemailCredentials = (text) => {
    const lines = text.trim().split('\n');
    const smtps = [];

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      let email, password;
      if (trimmed.includes(':')) {
        const parts = trimmed.split(':');
        email = parts[0].trim();
        password = parts.slice(1).join(':').trim();
      } else {
        const parts = trimmed.split(/\s+/);
        email = parts[0].trim();
        password = parts.slice(1).join(' ').trim();
      }

      if (!email || !password || !email.includes('@')) return;

      if (isMailComEmail(email)) {
        smtps.push({
          email,
          username: email,
          password,
          from_email: email,
          label: `Mail.com - ${email}`,
          provider: 'mailcom',
          host: 'smtp.mail.com',
          port: 587,
          encryption: 'tls',
          supports_dynamic_from: 0,
        });
        return;
      }

      if (isGmxEmail(email)) {
        smtps.push({
          email,
          username: email,
          password,
          from_email: email,
          label: `GMX - ${email}`,
          provider: 'gmx',
          host: resolveGmxSmtpHost(email),
          port: 587,
          encryption: 'tls',
          supports_dynamic_from: 0,
        });
        return;
      }

      if (isMailoEmail(email)) {
        smtps.push({
          email,
          username: email,
          password,
          from_email: email,
          label: `Mailo - ${email}`,
          provider: 'mailo',
          host: 'mail.mailo.com',
          port: 465,
          encryption: 'ssl',
          supports_dynamic_from: 0,
        });
      }
    });

    return smtps;
  };

  const parseTonlineCredentials = (text) => {
    const lines = text.trim().split('\n');
    const tonlineSmtps = [];
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) return;
      
      let email, password;
      
      if (trimmed.includes(':')) {
        const parts = trimmed.split(':');
        email = parts[0].trim();
        password = parts.slice(1).join(':').trim();
      } else if (trimmed.includes(' ')) {
        const spaceIdx = trimmed.indexOf(' ');
        email = trimmed.substring(0, spaceIdx).trim();
        password = trimmed.substring(spaceIdx + 1).trim();
      }
      
      if (email && password && email.includes('@')) {
        tonlineSmtps.push({
          email: email,
          username: email,
          password: password,
          from_email: email,
          label: `T-Online - ${email}`,
          host: 'securesmtp.t-online.de',
          port: 465,
          encryption: 'ssl',
          supports_dynamic_from: 0
        });
      }
    });
    
    return tonlineSmtps;
  };

  // Outlook/Hotmail SMTP Credentials parsen (mit OAuth Support!)
  const parseOutlookCredentials = (text) => {
    const lines = text.trim().split('\n');
    const outlookSmtps = [];
    let skippedNoOauth = 0;
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      // Format: email:password:oauth_token:client_id ODER email|password|oauth_token|client_id
      const delim = trimmed.includes('|') ? '|' : ':';
      
      // Client-ID am Ende finden (UUID-Format)
      const escapedDelim = delim === '|' ? '\\|' : ':';
      const clientIdRegex = new RegExp(escapedDelim + '([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$', 'i');
      const clientIdMatch = trimmed.match(clientIdRegex);
      if (!clientIdMatch) {
        skippedNoOauth++;
        return;
      }
      
      const clientId = clientIdMatch[1];
      const withoutClientId = trimmed.substring(0, trimmed.length - clientId.length - 1);
      
      const firstDelim = withoutClientId.indexOf(delim);
      if (firstDelim === -1) return;
      
      const email = withoutClientId.substring(0, firstDelim).trim();
      const rest1 = withoutClientId.substring(firstDelim + 1);
      
      const secondDelim = rest1.indexOf(delim);
      if (secondDelim === -1) { skippedNoOauth++; return; }
      
      const password = rest1.substring(0, secondDelim).trim();
      const oauthToken = rest1.substring(secondDelim + 1).trim();
      
      if (!email || !email.includes('@') || !oauthToken || oauthToken.length < 20) {
        skippedNoOauth++;
        return;
      }
      
      outlookSmtps.push({
        email, username: email, password, from_email: email,
        label: `Outlook OAuth - ${email}`,
        host: 'smtp-mail.outlook.com', port: 587, encryption: 'tls',
        supports_dynamic_from: 0,
        oauth_token: oauthToken, client_id: clientId, auth_type: 'oauth'
      });
    });
    
    if (skippedNoOauth > 0) {
      alert(`${skippedNoOauth} Outlook-Account(s) übersprungen: OAuth Refresh-Token oder Client-ID fehlt.\n\nFormat: email:password:refresh_token:client_id`);
    }
    
    return outlookSmtps;
  };

  const parseRamblerCredentials = (text) => {
    const lines = text.trim().split('\n');
    const ramblerSmtps = [];
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) return;
      
      let email, password;
      if (trimmed.includes(':')) {
        const parts = trimmed.split(':');
        email = parts[0].trim();
        password = parts.slice(1).join(':').trim();
      } else if (trimmed.includes(' ')) {
        const spaceIdx = trimmed.indexOf(' ');
        email = trimmed.substring(0, spaceIdx).trim();
        password = trimmed.substring(spaceIdx + 1).trim();
      }
      
      if (email && password && email.includes('@')) {
        ramblerSmtps.push({
          email: email,
          username: email,
          password: password,
          from_email: email,
          label: `Rambler - ${email}`,
          host: 'smtp.rambler.ru',
          port: 465,
          encryption: 'ssl',
          supports_dynamic_from: 0
        });
      }
    });
    
    return ramblerSmtps;
  };

  const { domains, from_prefix, host, port, google_credentials, freemail_credentials, outlook_credentials, rambler_credentials, tonline_credentials, from_name } = formData;

  const handleSelectSmtpType = (type) => {
    setSmtpType(type);
    setPreview([]);
    setFormData((prev) => {
      if (type === 'freemail') {
        normalConfigRef.current = {
          host: prev.host,
          port: prev.port,
          encryption: prev.encryption,
          username: prev.username,
          password: prev.password,
          from_prefix: prev.from_prefix,
          from_name: prev.from_name
        };
        return {
          ...prev,
          host: 'mail.gmx.net',
          port: 587,
          encryption: 'tls',
          username: '',
          password: '',
          domains: '',
          from_prefix: 'kontakt'
        };
      }
      if (type === 'normal') {
        const restored = normalConfigRef.current || {};
        return {
          ...prev,
          host: restored.host || 'smtp-relay.brevo.com',
          port: restored.port || 587,
          encryption: restored.encryption || 'tls',
          username: restored.username || '',
          password: restored.password || '',
          from_prefix: restored.from_prefix || 'kontakt',
          from_name: restored.from_name || ''
        };
      }
      if (type === 'rambler') {
        normalConfigRef.current = {
          host: prev.host,
          port: prev.port,
          encryption: prev.encryption,
          username: prev.username,
          password: prev.password,
          from_prefix: prev.from_prefix,
          from_name: prev.from_name
        };
        return {
          ...prev,
          host: 'smtp.rambler.ru',
          port: 465,
          encryption: 'ssl',
          username: '',
          password: '',
          domains: '',
          from_prefix: 'kontakt'
        };
      }
      if (type === 'tonline') {
        normalConfigRef.current = {
          host: prev.host,
          port: prev.port,
          encryption: prev.encryption,
          username: prev.username,
          password: prev.password,
          from_prefix: prev.from_prefix,
          from_name: prev.from_name
        };
        return {
          ...prev,
          host: 'securesmtp.t-online.de',
          port: 465,
          encryption: 'ssl',
          username: '',
          password: '',
          domains: '',
          from_prefix: 'kontakt'
        };
      }
      return prev;
    });
  };

  // Domain-Liste extrahieren und Vorschau generieren
  const updatePreview = React.useCallback(() => {
    if (smtpType === 'google') {
      const googleSmtps = parseGoogleCredentials(google_credentials);
      setPreview(googleSmtps);
      return;
    }
    
    if (smtpType === 'freemail') {
      const freemailSmtps = parseFreemailCredentials(freemail_credentials);
      setPreview(freemailSmtps);
      return;
    }

    if (smtpType === 'outlook') {
      const outlookSmtps = parseOutlookCredentials(outlook_credentials);
      setPreview(outlookSmtps);
      return;
    }

    if (smtpType === 'rambler') {
      const ramblerSmtps = parseRamblerCredentials(rambler_credentials);
      setPreview(ramblerSmtps);
      return;
    }

    if (smtpType === 'tonline') {
      const tonlineSmtps = parseTonlineCredentials(tonline_credentials);
      setPreview(tonlineSmtps);
      return;
    }
    
    // Normale Domain-Liste
    if (!domains.trim()) {
      setPreview([]);
      return;
    }
    
    const parsedDomains = extractDomainsFromText(domains);
    console.log('🔍 Extracted domains:', parsedDomains); // Debug
    
    const previewData = parsedDomains.map((domain, index) => ({
      domain: domain,
      from_email: `${from_prefix}@${domain}`,
      label: `${domain} (Bulk ${index + 1})`,
      host: host,
      port: port,
      from_name: from_name || domain
    }));
    
    console.log('📋 Preview data:', previewData); // Debug
    setPreview(previewData);
  }, [smtpType, domains, from_prefix, host, port, google_credentials, freemail_credentials, outlook_credentials, rambler_credentials, tonline_credentials, from_name]);

  // Domain-Extraktion (1:1 wie PHP-System)
  const extractDomainsFromText = (text) => {
    const lines = text.split('\n');
    const domains = [];
    
    // Regex für Domain-Erkennung (exakt wie PHP)
    const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    
    lines.forEach(line => {
      const trimmed = line.trim();
      
      console.log('🔍 Processing line:', `"${trimmed}"`); // Debug
      
      // Ignoriere leere Zeilen und Status-Texte (exakt wie PHP)
      if (!trimmed || 
          trimmed === 'Authentifiziert' || 
          trimmed === 'Authenticated' ||
          trimmed === 'Konfiguration ansehen' ||
          trimmed === 'View configuration' ||
          trimmed.includes('Konfiguration') ||
          trimmed.includes('Configuration') ||
          trimmed.includes('Status') ||
          trimmed.length < 4) {
        console.log('   → Skipped: status/info line');
        return;
      }
      
      // Prüfe ob es eine Domain ist (exakt wie PHP)
      if (domainRegex.test(trimmed)) {
        domains.push(trimmed);
        console.log('   → Found domain:', trimmed);
      } else {
        console.log('   → Not a valid domain');
      }
    });
    
    console.log('📋 Final extracted domains:', domains);
    
    // Duplikate entfernen
    return [...new Set(domains)];
  };

  // Vorschau bei Änderungen aktualisieren
  React.useEffect(() => {
    updatePreview();
  }, [updatePreview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let payload;
      
      if (smtpType === 'normal') {
        payload = {
          smtp_type: 'normal',
          host: formData.host,
          port: formData.port,
          username: formData.username,
          password: formData.password,
          encryption: formData.encryption,
          domains: formData.domains,
          from_name: formData.from_name,
          from_prefix: formData.from_prefix
        };
      } else if (smtpType === 'google') {
        payload = {
          smtp_type: 'google',
          google_credentials: formData.google_credentials,
          from_name: formData.from_name
        };
      } else if (smtpType === 'freemail') {
        payload = {
          smtp_type: 'freemail',
          freemail_credentials: formData.freemail_credentials,
          from_name: formData.from_name
        };
      } else if (smtpType === 'outlook') {
        const parsed = parseOutlookCredentials(formData.outlook_credentials);
        if (parsed.length === 0) {
          toast.error('Keine gültigen Outlook-Accounts mit OAuth-Daten gefunden. Format: email:password:refresh_token:client_id');
          setLoading(false);
          return;
        }
        payload = {
          smtp_type: 'outlook',
          outlook_credentials: formData.outlook_credentials,
          from_name: formData.from_name
        };
      } else if (smtpType === 'rambler') {
        payload = {
          smtp_type: 'rambler',
          rambler_credentials: formData.rambler_credentials,
          from_name: formData.from_name
        };
      } else if (smtpType === 'tonline') {
        payload = {
          smtp_type: 'tonline',
          tonline_credentials: formData.tonline_credentials,
          from_name: formData.from_name
        };
      }
      
      const response = await axios.post('/api/smtp/bulk', payload);

      if (response.data.success) {
        const { created_count: createdCount = 0, failed_count: failedCount = 0 } = response.data.data || {};
        if (createdCount === 0 && failedCount > 0) {
          toast.error(`❌ 0 erstellt — ${failedCount} Account(s) haben den SMTP-Auth-Test nicht bestanden`);
        } else if (createdCount === 0) {
          toast.error('❌ 0 SMTP-Server erstellt — Credentials-Format prüfen');
        } else if (failedCount > 0) {
          toast.success(`✅ ${createdCount} erstellt, ${failedCount} fehlgeschlagen (Auth-Test)`);
          onSuccess();
          onClose();
        } else {
          toast.success(`✅ ${createdCount} SMTP-Server erfolgreich erstellt!`);
          onSuccess();
          onClose();
        }
      } else {
        toast.error(`❌ Fehler: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Bulk SMTP creation error:', error);
      toast.error('Fehler beim Erstellen der SMTP-Server');
    } finally {
      setLoading(false);
    }
  };

  const exampleDomains = `send.autokauf-verhandeln.de
Authenticated

View configuration

audiag.autokauf-verhandeln.de
Authenticated

View configuration

audi.autokauf-verhandeln.de
Authenticated

View configuration

newsletter.autokauf-verhandeln.de
Authenticated

View configuration

campaigns.autokauf-verhandeln.de
Authenticated

View configuration

Das System extrahiert automatisch nur die Domains!`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-secondary rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border dark:border-dark-copper-mid">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-dark-copper-dark">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
            <Server className="h-5 w-5 mr-2 text-blue-600" />
            📦 Bulk SMTP-Server hinzufügen
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-bulk-smtp-build="20260711-tonline">
          {/* SMTP-Type Auswahl */}
          <div className="bg-purple-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              📧 SMTP-Typ auswählen
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => handleSelectSmtpType('normal')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  smtpType === 'normal' 
                    ? 'border-blue-500 dark:border-dark-copper-glow bg-blue-50 dark:bg-dark-surface' 
                    : 'border-gray-300 dark:border-dark-copper-dark hover:border-gray-400 dark:hover:border-dark-copper-mid'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-200">🔧 Normale SMTP</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">Brevo, AWS SES</div>
              </button>
              
              <button
                type="button"
                onClick={() => handleSelectSmtpType('google')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  smtpType === 'google' 
                    ? 'border-green-500 dark:border-dark-copper-glow bg-green-50 dark:bg-dark-surface' 
                    : 'border-gray-300 dark:border-dark-copper-dark hover:border-gray-400 dark:hover:border-dark-copper-mid'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-200">📧 Gmail</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">App-Passwörter</div>
              </button>
              
              <button
                type="button"
                onClick={() => handleSelectSmtpType('freemail')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  smtpType === 'freemail'
                    ? 'border-orange-500 dark:border-dark-copper-glow bg-orange-50 dark:bg-dark-surface'
                    : 'border-gray-300 dark:border-dark-copper-dark hover:border-gray-400 dark:hover:border-dark-copper-mid'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-200">📧 GMX / Mail.com / Mailo</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">gemischter Bulk-Import</div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectSmtpType('outlook')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  smtpType === 'outlook' 
                    ? 'border-blue-600 dark:border-dark-copper-glow bg-blue-50 dark:bg-dark-surface' 
                    : 'border-gray-300 dark:border-dark-copper-dark hover:border-gray-400 dark:hover:border-dark-copper-mid'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-200">📧 Outlook</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">Hotmail/Live</div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectSmtpType('rambler')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  smtpType === 'rambler' 
                    ? 'border-red-500 dark:border-dark-copper-glow bg-red-50 dark:bg-dark-surface' 
                    : 'border-gray-300 dark:border-dark-copper-dark hover:border-gray-400 dark:hover:border-dark-copper-mid'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-200">📧 Rambler</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">smtp.rambler.ru</div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectSmtpType('tonline')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  smtpType === 'tonline' 
                    ? 'border-pink-500 dark:border-dark-copper-glow bg-pink-50 dark:bg-dark-surface' 
                    : 'border-gray-300 dark:border-dark-copper-dark hover:border-gray-400 dark:hover:border-dark-copper-mid'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-200">📧 T-Online</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">securesmtp.t-online.de</div>
              </button>
            </div>
          </div>
        
          {/* SMTP-Zugangsdaten */}
          {smtpType === 'normal' && (
          <div className="bg-blue-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              🔧 SMTP-Zugangsdaten
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  SMTP-Host *
                </label>
                <input
                  type="text"
                  value={formData.host}
                  onChange={(e) => setFormData({...formData, host: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="smtp-relay.brevo.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Port *
                </label>
                <input
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Benutzername *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="9875a0001@smtp-brevo.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Passwort *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                    placeholder="68UkGEYypz7I9VPA"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Google SMTP Credentials */}
          {smtpType === 'google' && (
          <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              📧 Google App-Passwörter
            </h4>
            <textarea
              value={formData.google_credentials}
              onChange={(e) => {
                setFormData({...formData, google_credentials: e.target.value});
                setTimeout(updatePreview, 100);
              }}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm"
              placeholder={`iffath315@gmail.com suwx nqas oeky maql
Babamamashr4@gmail.com:gihihiy374:eberechispence@gmail.com:3nmxtt36gt3z4xeoz3nojmeezeqajwdk:rlmw zkgy qjov wpva

Format A: email@gmail.com app-password
Format B: email:login-pass:recovery:token:app-password mit Leerzeichen`}
              required
            />
            <small className="text-gray-600 dark:text-gray-200 mt-2 block">
              💡 App-Passwort-Leerzeichen werden automatisch entfernt. Extended-Format nutzt Feld 1 als Gmail + letztes Feld als App-PW.
            </small>
          </div>
          )}

          {/* GMX / Mail.com / Mailo SMTP Credentials */}
          {smtpType === 'freemail' && (
          <div className="bg-orange-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              📧 GMX / Mail.com / Mailo Accounts
            </h4>
            <textarea
              value={formData.freemail_credentials}
              onChange={(e) => {
                setFormData({...formData, freemail_credentials: e.target.value});
                setTimeout(updatePreview, 100);
              }}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
              placeholder={`max.mustermann@gmx.de:MeinPasswort123
anna.schmidt@mail.com:SuperSicher!456
leo.lewis94@mailo.com:MailoPasswort123
nina.moore@netc.fr:NochEinPasswort
info@gmx.net Passwort789

GMX, Mail.com und alle Mailo-Domains können gemischt importiert werden.`}
              required
            />
            <small className="text-gray-600 dark:text-gray-200 mt-2 block">
              💡 Format: <code className="bg-orange-100 px-1 py-0.5 rounded">email:passwort</code> oder <code className="bg-orange-100 px-1 py-0.5 rounded">email passwort</code> — Host wird automatisch gesetzt (GMX → mail.gmx.net/.com, Mail.com → smtp.mail.com, Mailo → mail.mailo.com:465 SSL)
            </small>
          </div>
          )}

          {/* Outlook/Hotmail SMTP Credentials */}
          {smtpType === 'outlook' && (
          <div className="bg-blue-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              📧 Outlook/Hotmail Accounts (OAuth2 Pflicht)
            </h4>
            <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>⚠️ Microsoft verlangt OAuth2</strong> — Accounts ohne <code>refresh_token</code> und <code>client_id</code> werden abgelehnt. Basic Auth ist dauerhaft deaktiviert.
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                Format: <code>email:password:refresh_token:client_id</code> (oder mit | als Trenner)
              </p>
            </div>
            <textarea
              value={formData.outlook_credentials}
              onChange={(e) => {
                setFormData({...formData, outlook_credentials: e.target.value});
                setTimeout(updatePreview, 100);
              }}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder={`email@outlook.com:password:M.C4_sAe...refresh_token...:a]b1c2d3-e4f5-6789-abcd-ef0123456789
email@hotmail.com:pw:eyJ0eXA...token...:a1b2c3d4-e5f6-7890-abcd-ef0123456789

⚠️ OAuth2 PFLICHT — Format: email:password:refresh_token:client_id
Microsoft blockiert Basic Auth, ohne OAuth geht nichts!`}
              required
            />
            <small className="text-gray-600 dark:text-gray-200 mt-2 block">
              💡 Funktioniert mit Outlook.com, Hotmail.com und Live.com Adressen
            </small>
          </div>
          )}

          {/* Rambler SMTP Credentials */}
          {smtpType === 'rambler' && (
          <div className="bg-red-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              📧 Rambler Mail Accounts
            </h4>
            <textarea
              value={formData.rambler_credentials}
              onChange={(e) => {
                setFormData({...formData, rambler_credentials: e.target.value});
                setTimeout(updatePreview, 100);
              }}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
              placeholder={`user1@rambler.ru:Passwort123
user2@rambler.ru:SuperSicher456

Format: email:passwort oder email passwort`}
              required
            />
            <small className="text-gray-600 dark:text-gray-200 mt-2 block">
              💡 Rambler.ru Accounts — SMTP: smtp.rambler.ru:465 (SSL)
            </small>
          </div>
          )}

          {/* T-Online SMTP Credentials */}
          {smtpType === 'tonline' && (
          <div className="bg-pink-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              📧 T-Online Mail Accounts
            </h4>
            <textarea
              value={formData.tonline_credentials}
              onChange={(e) => {
                setFormData({...formData, tonline_credentials: e.target.value});
                setTimeout(updatePreview, 100);
              }}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 font-mono text-sm"
              placeholder={`max.mustermann@t-online.de:MeinPasswort123
anna.schmidt@t-online.de:SuperSicher!456
info@t-online.de Passwort789

Oder mit Leerzeichen:
max.mustermann@t-online.de MeinPasswort123`}
              required
            />
            <small className="text-gray-600 dark:text-gray-200 mt-2 block">
              💡 Format: <code className="bg-pink-100 px-1 py-0.5 rounded">email:passwort</code> oder <code className="bg-pink-100 px-1 py-0.5 rounded">email passwort</code> — SMTP: securesmtp.t-online.de:465 (SSL)
            </small>
          </div>
          )}

          {/* Domain-Liste (nur bei normalen SMTPs) */}
          {smtpType === 'normal' && (
          <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              📋 Domain-Liste (einfach reinkopieren)
            </h4>
            <textarea
              value={formData.domains}
              onChange={(e) => setFormData({...formData, domains: e.target.value})}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder={exampleDomains}
              required
            />
            <small className="text-gray-600 dark:text-gray-200 mt-2 block">
              💡 System parsed automatisch nur die Domains aus Ihrer Liste
            </small>
          </div>
          )}

          {/* Absender-Einstellungen (nur bei normalen SMTPs) */}
          {smtpType === 'normal' && (
          <div className="bg-yellow-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              📤 Absender-Einstellungen
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Absender-Name
                </label>
                <input
                  type="text"
                  value={formData.from_name}
                  onChange={(e) => setFormData({...formData, from_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Wird aus Domain generiert"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  E-Mail-Prefix
                </label>
                <input
                  type="text"
                  value={formData.from_prefix}
                  onChange={(e) => setFormData({...formData, from_prefix: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="kontakt"
                />
                <small className="text-gray-500 dark:text-gray-300 text-xs mt-1">
                  Wird zu: {formData.from_prefix}@domain.de
                </small>
              </div>
            </div>
          </div>
          )}

          {/* Vorschau */}
          {preview.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                👁️ Vorschau ({preview.length} Server)
              </h4>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {preview.map((server, index) => {
                  const primaryLabel = smtpType === 'normal'
                    ? server.domain
                    : (server.from_email || server.email || server.username);
                  
                  const secondaryLabel = smtpType === 'normal'
                    ? `${server.from_email} • ${server.host}:${server.port}`
                    : `${server.provider === 'mailcom' ? 'Mail.com' : server.provider === 'gmx' ? 'GMX' : server.provider === 'mailo' ? 'Mailo' : (server.label || '').split(' - ')[0] || 'SMTP'} • ${server.from_email || server.email || server.username} • ${server.host}:${server.port}`;
                  
                  return (
                    <div key={index} className="flex items-center space-x-3 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium">{primaryLabel}</span>
                      <span className="text-gray-500 dark:text-gray-200">
                        {secondaryLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading || preview.length === 0}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Erstelle Server...' : `${preview.length} SMTP-Server erstellen`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkSmtpCreator;
